import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useApiClient } from '../utils/apiClient';

interface CurrencySettings {
  symbol: string;
  code: string;
  locale: string;
  position: 'before' | 'after';
}

interface CurrencyContextType {
  currency: CurrencySettings;
  formatCurrency: (amount: number) => string;
  /**
   * Compact currency for dense table cells — abbreviates large figures
   * (640000 → £640k, 1_200_000 → £1.2m) while small ones render in full.
   * Pair it with a tooltip showing `formatCurrency` for the exact value.
   */
  formatCompactCurrency: (amount: number) => string;
  /**
   * Currency at the precision the figure actually carries: pence shown only
   * when there are pence (`11001` → `£11,001`, `11001.2` → `£11,001.20`).
   *
   * For the surfaces whose claim is that **the parts add up** — the composition
   * cards and the workings drawer (Financial Integrity AC-21, AC-25). The
   * engine ties those figures to each other at the penny; rounding them to
   * whole pounds for display can print `£7,001 + £4,001` under a total of
   * `£11,001`, which is the card contradicting itself in the one place whose
   * entire job is reconciliation. Everywhere else keeps `formatCurrency`'s
   * whole units — a headline figure does not need its pence.
   *
   * Same "pence only when present" convention as the engine's own revenue rule
   * sentence, so a cap reads `£50,000` in both places.
   */
  formatCurrencyExact: (amount: number) => string;
  /**
   * Currency at a fixed number of decimals, for a column of rates that reads
   * one way down its length (`columnMoneyDigits` decides the digits): £650.00
   * beside £612.50, never £650 beside it.
   */
  formatCurrencyFixed: (amount: number, digits: number) => string;
  /**
   * Saves the default currency against the edit token the screen loaded
   * (`version`, which defaults to the context's own) and returns the new one.
   * A refusal because someone saved first rejects with `err.staleEdit`.
   */
  updateCurrency: (settings: CurrencySettings, version?: number | null) => Promise<{ version: number }>;
  /** Edit token of the stored setting; null until it has loaded, 0 when never saved. */
  currencyVersion: number | null;
  /**
   * Re-reads the setting without the loading state and makes it the app's
   * currency: the provider holds it for the whole session, so a save refused
   * as stale must bring it up to date as well as the screen.
   */
  refreshCurrency: () => Promise<{ currency: CurrencySettings; version: number }>;
  loading: boolean;
  error: string | null;
}

const defaultCurrency: CurrencySettings = {
  symbol: '£',
  code: 'GBP',
  locale: 'en-GB',
  position: 'before',
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { getAccessToken, isAuthenticated } = useAuth();
  const apiClient = useApiClient(getAccessToken);
  const [currency, setCurrency] = useState<CurrencySettings>(defaultCurrency);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<number | null>(null);

  const adopt = (settings: CurrencySettings & { version?: number }) => {
    const { version: next, ...rest } = settings;
    setCurrency(rest);
    setVersion(next ?? 0);
    return { currency: rest, version: next ?? 0 };
  };

  const refreshCurrency = async () => {
    const response = await apiClient.makeApiCall('/api/settings/currency');
    if (!response.ok) throw new Error('Failed to load currency settings');
    return adopt(await response.json());
  };

  const loadCurrency = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.makeApiCall('/api/settings/currency');
      
      if (response.ok) {
        adopt(await response.json());
      } else if (response.status === 404) {
        // No currency setting found, use default
        setCurrency(defaultCurrency);
      } else {
        throw new Error('Failed to load currency settings');
      }
    } catch (err) {
      console.error('Error loading currency settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load currency settings');
      // Fall back to default currency on error
      setCurrency(defaultCurrency);
    } finally {
      setLoading(false);
    }
  };

  const updateCurrency = async (settings: CurrencySettings, expected?: number | null) => {
    try {
      setError(null);
      const saved = await apiClient.put<CurrencySettings & { version: number }>('/api/settings/currency', {
        ...settings,
        version: expected ?? version,
      });
      return { version: adopt({ ...settings, ...saved }).version };
    } catch (err) {
      // A stale refusal is the panel's to explain (banner); the provider's own
      // error slot is for loads.
      if (!(err as { staleEdit?: unknown })?.staleEdit) {
        setError(err instanceof Error ? err.message : 'Failed to update currency settings');
      }
      throw err;
    }
  };

  const formatCurrency = (amount: number): string => {
    if (isNaN(amount)) return 'N/A';
    
    try {
      // Use the built-in Intl.NumberFormat for proper formatting
      const formatter = new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        maximumFractionDigits: 0,
      });
      
      return formatter.format(amount);
    } catch (_err) {
      // Fallback formatting if Intl.NumberFormat fails
      const formattedAmount = amount.toLocaleString();
      return currency.position === 'before' 
        ? `${currency.symbol}${formattedAmount}`
        : `${formattedAmount}${currency.symbol}`;
    }
  };

  const formatCurrencyExact = (amount: number): string => {
    if (isNaN(amount)) return 'N/A';
    // `Number.isInteger` on the figure itself, not on a re-rounded copy: every
    // money figure reaching the browser has already crossed the engine's one
    // rounding boundary (AC-35), so a fractional part here is real pence rather
    // than float dust.
    const digits = Number.isInteger(amount) ? 0 : 2;
    try {
      return new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount);
    } catch (_err) {
      const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      return currency.position === 'before'
        ? `${currency.symbol}${formatted}`
        : `${formatted}${currency.symbol}`;
    }
  };

  const formatCurrencyFixed = (amount: number, digits: number): string => {
    if (isNaN(amount)) return 'N/A';
    try {
      return new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(amount);
    } catch (_err) {
      const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      return currency.position === 'before'
        ? `${currency.symbol}${formatted}`
        : `${formatted}${currency.symbol}`;
    }
  };

  const formatCompactCurrency = (amount: number): string => {
    if (isNaN(amount)) return 'N/A';
    try {
      // Format the magnitude compactly (640k / 1.2m), then apply the
      // workspace symbol + position — mirrors `formatCurrency`'s fallback so
      // letter-code currencies (US$, CA$) aren't mangled by lower-casing.
      // The magnitude is unsigned and the sign leads the whole figure:
      // formatting the signed amount would put it inside the symbol (`£-1.2m`).
      const num = new Intl.NumberFormat(currency.locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      })
        .format(Math.abs(amount))
        .toLowerCase();
      // A figure that rounds to nothing has no sign: −0.04 reads £0, not "-£0".
      const sign = amount < 0 && /[1-9]/.test(num) ? '-' : '';
      return currency.position === 'before'
        ? `${sign}${currency.symbol}${num}`
        : `${sign}${num}${currency.symbol}`;
    } catch {
      return formatCurrency(amount);
    }
  };

  useEffect(() => {
    loadCurrency();
  }, [isAuthenticated]);

  const value: CurrencyContextType = {
    currency,
    formatCurrency,
    formatCompactCurrency,
    formatCurrencyExact,
    formatCurrencyFixed,
    updateCurrency,
    currencyVersion: version,
    refreshCurrency,
    loading,
    error,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Predefined currency options
export const CURRENCY_OPTIONS: CurrencySettings[] = [
  { symbol: '£', code: 'GBP', locale: 'en-GB', position: 'before' },
  { symbol: '$', code: 'USD', locale: 'en-US', position: 'before' },
  { symbol: '€', code: 'EUR', locale: 'en-EU', position: 'before' },
  { symbol: '¥', code: 'JPY', locale: 'ja-JP', position: 'before' },
  { symbol: 'CA$', code: 'CAD', locale: 'en-CA', position: 'before' },
  { symbol: 'A$', code: 'AUD', locale: 'en-AU', position: 'before' },
]; 